import axios from 'axios';

// Determine base URL
let baseURL = "";
if (window.location.hostname === "localhost") {
    baseURL = "http://localhost:3050";
} else {
    baseURL = window.location.origin;
}

// Error Types Enum
export const ErrorTypes = {
    UNAUTHORIZED: 'UNAUTHORIZED',           // 401
    FORBIDDEN: 'FORBIDDEN',                 // 403
    NOT_FOUND: 'NOT_FOUND',                 // 404
    BAD_REQUEST: 'BAD_REQUEST',             // 400
    SERVER_ERROR: 'SERVER_ERROR',           // 500
    NETWORK_ERROR: 'NETWORK_ERROR',         // No response
    TIMEOUT: 'TIMEOUT',                     // Request timeout
    UNKNOWN: 'UNKNOWN'
};

// Create axios instance
const axiosInstance = axios.create({
    baseURL: baseURL,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    }
});

// REQUEST INTERCEPTOR
axiosInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken') || sessionStorage.getItem('Token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        const projectIndex = localStorage.getItem('selectedProjectIndex') || '0';
        config.headers.index = projectIndex;

        return config;
    },
    (error) => {

        return Promise.reject(error);
    }
);

// RESPONSE INTERCEPTOR - OAS Compliant
axiosInstance.interceptors.response.use(
    (response) => {
        // SUCCESS Response - Standardize OAS format
        const standardizedResponse = {
            success: response.data?.success ?? true,
            data: response.data?.data ?? response.data,
            message: response.data?.message ?? 'Operation successful',
            code: response.status,
            errorType: null,
            meta: response.data?.meta ?? {
                timestamp: new Date().toISOString(),
                statusCode: response.status
            },
            // Legacy fields for backward compatibility
            status: response.data?.status ?? "done",
            result: response.data?.result ?? response.data?.data
        };

        
        return standardizedResponse;
    },
    (error) => {
        // ERROR Response Handler
        let standardizedError = {
            success: false,
            data: null,
            message: 'An error occurred',
            code: 500,
            errorType: ErrorTypes.UNKNOWN,
            errorDetails: null,
            shouldShowDialog: true,
            status: "fail",
            error: null
        };

        if (error.response) {
            const status = error.response.status;
            const responseData = error.response.data;

            let errorType = ErrorTypes.UNKNOWN;
            let defaultMessage = 'An error occurred';

            switch (status) {
                case 400:
                    errorType = ErrorTypes.BAD_REQUEST;
                    defaultMessage = 'Invalid request';
                    break;
                case 401:
                    errorType = ErrorTypes.UNAUTHORIZED;
                    defaultMessage = 'Your session has expired. Please login again.';
                    setTimeout(() => {
                        localStorage.removeItem('authToken');
                        sessionStorage.removeItem('Token');
                        window.location.href = '/login';
                    }, 2000);
                    break;
                case 403:
                    errorType = ErrorTypes.FORBIDDEN;
                    defaultMessage = 'You do not have permission to perform this action';
                    break;
                case 404:
                    errorType = ErrorTypes.NOT_FOUND;
                    defaultMessage = 'Resource not found';
                    break;
                case 500:
                case 502:
                case 503:
                case 504:
                    errorType = ErrorTypes.SERVER_ERROR;
                    defaultMessage = 'Server error. Please try again later';
                    break;
            }

            standardizedError = {
                success: false,
                data: responseData?.data ?? null,
                message: responseData?.message ?? responseData?.error?.detail ?? defaultMessage,
                code: status,
                errorType: errorType,
                errorDetails: responseData?.error ?? {
                    code: responseData?.error?.code ?? errorType,
                    title: error.response.statusText,
                    detail: responseData?.message ?? defaultMessage
                },
                shouldShowDialog: true,
                status: "fail",
                error: responseData?.message ?? responseData?.error ?? defaultMessage
            };

        } else if (error.request) {
            standardizedError = {
                success: false,
                data: null,
                message: 'Network error. Please check your internet connection.',
                code: 0,
                errorType: ErrorTypes.NETWORK_ERROR,
                errorDetails: {
                    code: 'NETWORK_ERROR',
                    title: 'Network Error',
                    detail: 'No response from server'
                },
                shouldShowDialog: true,
                status: "fail",
                error: 'Network error'
            };
        } else if (error.code === 'ECONNABORTED') {
            standardizedError = {
                success: false,
                data: null,
                message: 'Request timeout. Please try again.',
                code: 0,
                errorType: ErrorTypes.TIMEOUT,
                errorDetails: {
                    code: 'TIMEOUT',
                    title: 'Request Timeout',
                    detail: 'The request took too long'
                },
                shouldShowDialog: true,
                status: "fail",
                error: 'Request timeout'
            };
        }

        console.error(' Error Response:', error.config?.url, standardizedError);
        return Promise.resolve(standardizedError);
    }
);

export default axiosInstance;
export { baseURL };
